import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { BrandLogo } from "@/components/BrandLogo";
import { 
  LayoutDashboard, 
  BookOpen, 
  BarChart3,
  Clock,
  LogOut,
  GraduationCap,
  Briefcase,
  Sparkles,
  Sun,
  Moon,
  Shield,
  CreditCard,
  LifeBuoy,
  Users,
  Timer,
  Gift,
} from "lucide-react";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Home", hint: "Your overview" },
  { path: "/learn", icon: GraduationCap, label: "Learn", hint: "Read & study" },
  { path: "/practice", icon: BookOpen, label: "Practice", hint: "Answer questions" },
  { path: "/cases", icon: Briefcase, label: "Cases", hint: "Clinical scenarios" },
  { path: "/mock-exam", icon: Clock, label: "Mock Exam", hint: "Real exam practice" },
  { path: "/trials", icon: Timer, label: "Trials", hint: "Full readiness trials" },
  { path: "/community", icon: Users, label: "Community", hint: "Fellow candidates" },
  { path: "/progress", icon: BarChart3, label: "My Stats", hint: "Track progress" },
  { path: "/account?tab=refer", icon: Gift, label: "Refer & Earn", hint: "Give 14 days, get 14 days" },
  { path: "/account?tab=support", icon: LifeBuoy, label: "Support", hint: "Get help" },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isPro, isTrialing, trialDaysLeft } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="sidebar hidden md:flex flex-col fixed left-0 top-0 h-full w-20 hover:w-64 bg-card border-r border-border z-50 group transition-all duration-200">
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <BrandLogo size={44} className="flex-shrink-0" />
          <span className="sidebar-label font-bold text-lg whitespace-nowrap">PassADC</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => {
            const [itemPath, itemQuery] = item.path.split("?");
            const itemTabValue = itemQuery ? new URLSearchParams(itemQuery).get("tab") : null;
            const currentTab = new URLSearchParams(location.search).get("tab");
            const isActive = itemTabValue
              ? (location.pathname === itemPath && currentTab === itemTabValue)
              : (location.pathname === itemPath ||
                 (itemPath !== "/dashboard" && location.pathname.startsWith(itemPath)));
            
            return (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                    ${isActive 
                      ? 'bg-primary/15 text-primary font-medium' 
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}
                  `}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="sidebar-label whitespace-nowrap">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Pro CTA / User info */}
      <div className="p-4 border-t border-border space-y-2">
        {user?.role === "admin" && (
          <button
            onClick={() => navigate("/admin")}
            data-testid="sidebar-admin-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary transition-all border border-primary/30"
          >
            <Shield className="w-5 h-5 flex-shrink-0" />
            <span className="sidebar-label whitespace-nowrap font-semibold">Admin Portal</span>
          </button>
        )}
        {isTrialing && user?.role !== "admin" && (
          <div
            data-testid="sidebar-trial-days"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 text-primary border border-primary/25"
          >
            <Sparkles className="w-5 h-5 flex-shrink-0" />
            <span className="sidebar-label whitespace-nowrap font-semibold">
              Pro trial: {trialDaysLeft} {trialDaysLeft === 1 ? "day" : "days"} left
            </span>
          </div>
        )}
        {!isPro && (
          <button
            onClick={() => navigate("/billing")}
            data-testid="sidebar-upgrade-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-accent/10 hover:bg-accent/20 text-accent transition-all border border-accent/30"
          >
            <Sparkles className="w-5 h-5 flex-shrink-0" />
            <span className="sidebar-label whitespace-nowrap font-semibold">Upgrade to Pro</span>
          </button>
        )}
        {isPro && user?.role !== "admin" && user?.subscription_status === "cancelled" && (
          <button
            onClick={() => navigate("/account?tab=billing")}
            data-testid="sidebar-renew-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-warning/10 hover:bg-warning/20 text-warning transition-all border border-warning/30"
          >
            <CreditCard className="w-5 h-5 flex-shrink-0" />
            <span className="sidebar-label whitespace-nowrap font-semibold">Renew Pro</span>
          </button>
        )}
        {user && (
          <button
            onClick={() => navigate("/account")}
            data-testid="sidebar-account-btn"
            className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-left hover:bg-secondary transition-all"
          >
            <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center flex-shrink-0 text-sm font-semibold">
              {(user.name || user.email).charAt(0).toUpperCase()}
            </div>
            <div className="sidebar-label min-w-0">
              <p className="truncate font-medium text-foreground text-xs">{user.name || user.email}</p>
              <p className="truncate text-xs text-muted-foreground">{isPro ? "Pro member" : "Free tier"} · Manage →</p>
            </div>
          </button>
        )}
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle-button"
          className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground transition-all text-sm"
        >
          {theme === "dark" ? <Sun className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
          <span className="sidebar-label whitespace-nowrap">{theme === "dark" ? "Light mode" : "Dark mode"}</span>
        </button>
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout-btn"
          className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground transition-all text-sm"
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          <span className="sidebar-label whitespace-nowrap">Sign out</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
